<template>
  <div>
    <VForm :form="form">
      <VSelect v-model="selectedVendor" name="vendor" :label="$t('substore.manufacturer')">
        <option :disabled="selectedVendorExists" :value="selectedVendorExists ? '' : selectedVendor">{{ $t('substore.please_select') }}</option>
        <option v-for="(vendor, key) in vendors" :key="key" :value="key" :selected="selectedVendor === key">{{ vendor.name }}</option>
      </VSelect>

      <VSelect v-model="selectedDevice" name="device" :label="$t('substore.type_designation')" :disabled="!selectedVendorExists">
        <option :disabled="selectedDeviceExists" :value="selectedDeviceExists ? '' : selectedDevice">{{ $t('substore.please_select') }}</option>
        <option v-for="(device, key) in devices" :key="key" :value="key" :selected="selectedDevice === key">{{ device.name }}</option>
      </VSelect>

      <VSelect v-model="selectedTariff" name="tariff" :label="$t('substore.package')" :disabled="!selectedDeviceExists">
        <option :disabled="selectedTariffExists" :value="selectedTariffExists ? '' : selectedTariff">{{ $t('substore.please_select') }}</option>
        <option v-for="tariff in tariffs" :key="tariff.id" :value="tariff.insurance.id" :selected="selectedTariff === tariff.insurance.id">
          {{ tariff.insurance.name }}
        </option>
      </VSelect>
    </VForm>
  </div>
</template>

<script>
import Proxy from '@/proxies/SubstoreProductDetailsProxy';
import Form from '@/components/Form/Form';
import Select from '@/components/Form/Select';
import { initFields } from '@/utils/form';

export default {
  name: 'AceDeviceSelection',

  components: {
    VForm: Form,
    VSelect: Select,
  },

  data() {
    return {
      form: initFields({ name: 'vendor' }),
      priceList: null,
      selectedVendor: null,
      selectedDevice: null,
      selectedTariff: null,
    };
  },

  computed: {
    /**
     * [key1] = { name, devices }
     *
     * devices:
     * [key1_key2] = { name, id, classId }
     *
     * NOTE: keys are concatenated to ensure the selection gets removed when switching upper selections
     */
    vendors() {
      if (!this.priceList?.manufacturer) return [];
      const vendors = {};
      Object.values(this.priceList.manufacturer).forEach((v, index) => {
        const key = `vendor_${index}`;
        const devices = {};
        v.deviceList.device.forEach((d, index2) => {
          const key2 = `${key}_device_${index2}`;
          devices[key2] = { name: d.modelName, classId: d.deviceClassId, id: d.deviceId };
        });
        vendors[key] = { name: v.manufacturerName, devices };
      });
      return vendors;
    },

    devices() {
      return this.vendors?.[this.selectedVendor]?.devices;
    },

    insurances() {
      if (!this.priceList?.insuranceClass) return null;
      const insuranceClassNames = {};
      this.priceList.insuranceClass.forEach(i => {
        insuranceClassNames[i.id] = i.name;
      });
      return insuranceClassNames;
    },

    tariffs() {
      if (!this.priceList?.insuranceClass || !this.selectedDeviceExists) return null;
      const { classId } = this.devices[this.selectedDevice];

      return this.priceList.tariff
        .filter(t => t.deviceClassId === classId)
        .map(t => ({
          id: t.tariffId,
          insurance: {
            id: t.insuranceClassId,
            name: this.insurances[t.insuranceClassId],
          },
          price: t.Price.pricesMonthlyPayment.pricePerMonth,
        }));
    },

    result() {
      let result = null;
      if (this.selectedTariffExists) {
        result = {
          vendor: this.vendors[this.selectedVendor].name,
          device: this.devices[this.selectedDevice],
          tariff: this.tariffs?.find(t => t.insurance.id === this.selectedTariff),
        };
      }

      return result;
    },

    selectedVendorExists() {
      if (!this.vendors) return false;
      return !!Object.keys(this.vendors).some(k => k === this.selectedVendor);
    },

    selectedDeviceExists() {
      if (!this.devices) return false;
      return !!Object.keys(this.devices).some(k => k === this.selectedDevice);
    },

    selectedTariffExists() {
      if (!this.tariffs) return false;
      return !!Object.values(this.tariffs).some(v => v.insurance.id === this.selectedTariff);
    },
  },

  watch: {
    result() {
      this.$emit('result', this.result);
    },
  },

  async created() {
    const response = await new Proxy().partner('ace');
    this.priceList = Object.freeze(response.data.priceList);
  },
};
</script>
